import { useMemo } from "react";
import { isObject } from "lodash";

import { getState } from "../../../../state/store";
import {
  selectAppTypes,
  selectGroupsConfigs,
  selectLocations,
  selectMarkets
} from "../../../../state/selectors";

import { appTypesNames } from "../../../../consts";
import { appsUtil } from "../../../../utils";

export const useGetRoleConfigOptions = ({
  isPrepopulated,
  selectedApplication,
  selectedMarkets,
  selectedRole,
  selectedRolesList,
  setSelectedMarkets = () => {},
  setSelectedLocation = () => {}
}) => {
  const userApps = selectAppTypes()(getState()) ?? [];
  const marketConfigs = selectMarkets()(getState()) ?? [];
  const groupsConfigs = selectGroupsConfigs()(getState()) ?? [];

  const selectedRoles = useMemo(() => {
    if (selectedApplication) {
      return selectedRolesList
        ?.filter(
          (config) =>
            config?.application === selectedApplication?.id && config?.role !== selectedRole?.id
        )
        ?.map((config) => config?.role);
    }

    return [];
  }, [selectedApplication, selectedRole, selectedRolesList]);

  const appOptions = useMemo(() => {
    const availableApps = userApps
      .filter((app) => app !== appTypesNames.allApps)
      .map((app) => {
        return { id: app, value: appsUtil.getAppName(app) };
      });

    return availableApps;
  }, [userApps]);

  const marketOptions = useMemo(() => {
    if (!isPrepopulated) {
      setSelectedMarkets([]);
    }

    if (selectedApplication) {
      let markets = marketConfigs;

      markets = marketConfigs.filter((marketConfig) =>
        marketConfig?.activeAppTypes.includes(selectedApplication?.id)
      );

      return markets.map((marketConfig) => {
        return { id: marketConfig?.id, value: marketConfig?.id };
      });
    } else {
      return [];
    }
  }, [selectedApplication, marketConfigs]);

  const locationOptions = useMemo(() => {
    if (!isPrepopulated) {
      setSelectedLocation(null);
    }

    if (selectedMarkets?.length === 1) {
      const locationsByMarket = selectLocations(selectedMarkets[0].id)(getState());

      return (locationsByMarket ?? []).map((locationConfig) => {
        return { id: locationConfig.id, value: locationConfig?.name };
      });
    } else {
      return [];
    }
  }, [selectedMarkets]);

  const roleOptions = useMemo(() => {
    if (selectedApplication) {
      return (groupsConfigs[selectedApplication.id] ?? [])
        .filter((group) => !selectedRoles?.includes(group))
        .map((group) => {
          if (isObject(group)) {
            return { id: group?.group, value: group?.value };
          }

          return { id: group, value: group };
        });
    } else {
      return [];
    }
  }, [selectedApplication, groupsConfigs, selectedRoles]);

  return { appOptions, marketOptions, locationOptions, roleOptions };
};
